import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import cl from 'classnames';
import { ALL_ITEM_ID } from 'src/constants';
import { useListPosition } from 'src/hooks';
import { Checkbox } from 'src/components/general';
import styles from './groupFilterStyle.module.scss';

type GroupFilterProps = {
  label: string;
  selectedIDs: Set<string>;
  items: { id: string; label: string; inactive?: boolean }[];
  selectItem: (id: string, value: boolean) => void;
  disabled?: boolean | null;
  isLoading?: boolean;
  expandOnTop?: boolean;
  className?: string;
  size?: 'small' | 'medium';
  onExpanded?: (value: boolean) => void;
  hideCount?: boolean;
};

export const GroupFilter = ({
  label,
  selectedIDs,
  items,
  selectItem,
  disabled = false,
  isLoading = false,
  expandOnTop = false,
  className,
  size = 'medium',
  onExpanded,
  hideCount = false,
}: GroupFilterProps) => {
  const { isView, toHide, btnRef, portalRef, onBtnClick, listPosition } =
    useListPosition(expandOnTop);

  const actualSelectedIDs = useMemo(
    () =>
      new Set(
        [...selectedIDs].filter((selectedID) =>
          items.some(({ id }) => id === selectedID),
        ),
      ),
    [items, selectedIDs],
  );

  const count = useMemo(
    () => actualSelectedIDs.size - (actualSelectedIDs.has(ALL_ITEM_ID) ? 1 : 0),
    [actualSelectedIDs],
  );

  useEffect(() => {
    onExpanded?.(isView);
  }, [isView, onExpanded]);

  return (
    <div
      ref={btnRef}
      onClick={(event) => {
        if (isLoading || disabled) {
          return;
        }
        onBtnClick(event);
      }}
      className={cl(
        styles.button,
        size === 'small' && styles.small,
        disabled && styles.disabled,
        className,
      )}
    >
      {isLoading && (
        <div className={styles.loading}>
          <div className={styles.loader} />
        </div>
      )}
      <div className={styles.label}>
        {isLoading ? (
          ''
        ) : (
          <>
            {label}
            {!hideCount && Boolean(count) && ` (${count})`}
          </>
        )}
      </div>
      <svg
        width="14"
        height="16"
        viewBox="0 0 14 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cl(
          styles.arrowIcon,
          isView === Boolean(expandOnTop) && styles.expanded,
        )}
      >
        <path
          d="M2.91536 10.5923L6.9987 6.05525L11.082 10.5923"
          stroke="#323C46"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {listPosition &&
        createPortal(
          <div className={styles.portal} ref={portalRef} onClick={toHide}>
            <div
              className={styles.relativeContainer}
              onClick={toHide}
              style={{ ...listPosition }}
            >
              <div
                className={cl(
                  styles.absoluteContainer,
                  expandOnTop && styles.expandOnTop,
                )}
              >
                <div
                  className={cl(styles.listWrapper, isView && styles.expanded)}
                  style={{ width: listPosition.width }}
                >
                  <div
                    className={styles.list}
                    style={{ width: listPosition.width }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {items.map((listItem) => (
                      <Checkbox
                        key={listItem.id}
                        label={listItem.label}
                        checked={actualSelectedIDs.has(listItem.id)}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          selectItem(listItem.id, event.target.checked);
                        }}
                        className={styles.listItem}
                        disabled={listItem.inactive}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GroupFilter;
